import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, of } from 'rxjs';
import { AdvertCreatorFiltersDto, AdvertLocationDto, AdvertSectorDto, AdvertTypeItemDto, AdvertTypesDto, MultiplePropertyListingDto, PropertyListingDto, UserDto } from '../../../../../../app/services/property-matrixV2/models';
import { AdvertService, LookupService, PropertyListingService, UserManagementService } from '../../../../../../app/services/property-matrixV2/services';
import { ListingStateService } from '../../../../../../app/shared/services/listing-state-service/listing-state.service';
import { NotificationService } from '../../../../../../app/shared/services/notification-service/notification.service';

type AdvertFilterSector = 'Developed' | 'Developable';

interface AdvertTypeItemOption {
  id: string;
  name: string;
}

interface AdvertTypeSubgroupOption {
  title: string | null;
  items: AdvertTypeItemOption[];
}

interface AdvertTypeCategoryOption {
  category: string;
  expanded: boolean;
  subgroups: AdvertTypeSubgroupOption[];
}

function buildDevelopedTypeCategories(hierarchy: AdvertTypesDto[]): AdvertTypeCategoryOption[] {
  return hierarchy
    .filter(category => (category.subgroups ?? []).length > 0)
    .map((category, index) => ({
      category: category.description ?? '',
      expanded: index === 0,
      subgroups: (category.subgroups ?? []).map(subgroup => ({
        title: subgroup.description ?? null,
        items: (subgroup.items ?? [])
          .filter((item): item is AdvertTypeItemDto & { id: string } => !!item.id)
          .map(item => ({ id: item.id, name: item.description ?? '' })),
      })),
    }));
}

@Component({
  selector: 'app-advert-filter-create',
  templateUrl: './advert-filter-create.component.html',
  styleUrls: ['./advert-filter-create.component.scss', '../../../../../../css/2-modules/_admin-portal.scss']
})
export class AdvertFilterCreateComponent implements OnInit {
  @Input() selectedListing: PropertyListingDto;
  @Input() selectedListingGroup: MultiplePropertyListingDto;
  @Input() listingType: string;

  activeTab: AdvertFilterSector = 'Developed';
  typeSection = { expanded: true };
  priceSection = { expanded: true };
  sizeSection = { expanded: true };

  advertSectorList: AdvertSectorDto[] = [];
  advertTypesList: AdvertTypesDto[] = [];
  optOut: Record<AdvertFilterSector, boolean> = { Developed: false, Developable: false };
  developableTypeIds = new Set<string>();
  developedItemIds = new Set<string>();
  developedTypeCategories: AdvertTypeCategoryOption[] = [];
  private hierarchyCategoryIds = new Set<string>();
  loading: boolean = false;
  SgErfExtent: string | null | undefined;
  sellerAskingPriceExcludeVATcommission: number | null | undefined;
  selectedLocation: AdvertLocationDto[] = [];
  selectedSector: AdvertSectorDto[] = [];
  selectedTypes: AdvertTypesDto[] = [];
  advertFilterDTO: AdvertCreatorFiltersDto = {};
  currentUser: UserDto;
  advertFilterDTOid: string;
  private sectorsLoaded = false;
  private typesLoaded = false;
  private hierarchyLoaded = false;
  private prePopulatedDataLoaded = false;

  constructor(
    private lookupService: LookupService,
    private notificationService: NotificationService,
    private router: Router,
    private listingStateService: ListingStateService,
    private advertService: AdvertService,
    private userManagementService: UserManagementService,
    private propertyListingService: PropertyListingService,
  ) { }

  get otherTab(): AdvertFilterSector {
    return this.activeTab === 'Developed' ? 'Developable' : 'Developed';
  }

  get isActiveSectorOptedOut(): boolean {
    return this.optOut[this.activeTab];
  }

  get isActiveSectorMandatory(): boolean {
    return this.optOut[this.otherTab];
  }

  onOptOutChange(optedOut: boolean): void {
    this.optOut[this.activeTab] = optedOut;
    if (optedOut) {
      this.optOut[this.otherTab] = false;
      this.clearSelectionsForSector(this.activeTab);
    }
  }

  isTypeSelected(type: AdvertTypesDto): boolean {
    return !!type.id && this.developableTypeIds.has(type.id);
  }

  onTypeToggle(type: AdvertTypesDto, checked: boolean): void {
    if (!type.id) {
      return;
    }
    if (checked) {
      this.developableTypeIds.add(type.id);
    } else {
      this.developableTypeIds.delete(type.id);
    }
  }

  isDevelopedItemSelected(item: AdvertTypeItemOption): boolean {
    return this.developedItemIds.has(item.id);
  }

  onDevelopedItemToggle(item: AdvertTypeItemOption, checked: boolean): void {
    if (checked) {
      this.developedItemIds.add(item.id);
    } else {
      this.developedItemIds.delete(item.id);
    }
  }

  toggleDevelopedCategory(category: AdvertTypeCategoryOption): void {
    const wasOpen = category.expanded;
    this.developedTypeCategories.forEach(c => c.expanded = false);
    category.expanded = !wasOpen;
  }

  ngOnInit(): void {
    this.loadCheckboxes();
    this.getAdvertPrePopulatedData();
    this.getCurrentUser();
  }

  goToListingOverViewRoute(): void {
    this.router.navigate(['admin/listings/listing-overview'], {
      queryParams: {
        listingType: this.listingType
      }
    });
  }

  goToAdminListings() {
    this.router.navigate(['admin/listings']);
  }

  saveDetails() {
    this.loading = true;
    this.syncSectorsFromOptOut();
    this.selectedSector = this.getSelectedSectors();
    this.selectedTypes = this.getSelectedTypes();

    if (!this.advertFilterDTO) {
      this.advertFilterDTO = {};
    }

    this.advertFilterDTO.advertLocation = this.selectedLocation;
    this.advertFilterDTO.advertSector = this.selectedSector;
    this.advertFilterDTO.advertTypes = this.selectedTypes;
    this.advertFilterDTO.advertTypeItems = this.getSelectedDevelopedItems();
    this.advertFilterDTO.sellerAskingPriceExcludeVATCommission = this.sellerAskingPriceExcludeVATcommission;
    this.advertFilterDTO.sgErfExtent = this.SgErfExtent;
    this.advertFilterDTO.isDeleted = false;
    this.advertFilterDTO.propertyListingId = this.selectedListing.id;
    this.advertFilterDTO.id = this.advertFilterDTOid;

    return this.advertService.apiV1AdvertSaveAdvertDataPost({
      body: this.advertFilterDTO
    }).pipe(
      catchError(() => {
        this.notificationService.showErrorMessage("Error", "Could not save advert filter.");
        return of(false);
      }),
      finalize(() => this.loading = false)
    );
  }

  saveConfirmDetails(): void {

    this.saveDetails().subscribe(saved => {

      if (!saved) {
        this.notificationService.showErrorMessage("Error", "Could not save advert filters.");
        return;
      }

      this.propertyListingService
        .apiV1PropertyListingGetPropertyListingByIdGet({ listingId: this.selectedListing.id })
        .subscribe(freshListing => {

          this.selectedListing = freshListing;
          this.listingStateService.setSelectedListing(this.selectedListing);

          this.setPropertyListingTask();
          this.notificationService.showSuccessMessage("Saved", "Task successfully completed.");
        });

    });
  }

  saveData(): void {
    this.saveDetails().subscribe(saved => {
      if (saved) {
        this.listingStateService.setSelectedListing(this.selectedListing);
        this.notificationService.showSuccessMessage("Saved", "Details successfully completed.");
      } else {
        this.notificationService.showErrorMessage("Error", "Could not save advert filters");
      }
    });
  }

  getSelectedTypes(): AdvertTypesDto[] {
    return this.advertTypesList
      .filter(type => !!type.id && this.developableTypeIds.has(type.id))
      .map(type => ({ ...type, isChecked: true }));
  }

  getSelectedDevelopedItems(): AdvertTypeItemDto[] {
    return Array.from(this.developedItemIds).map(id => ({ id }));
  }

  getSelectedSectors() {
    return this.advertSectorList.filter(sector => sector.isChecked);
  }

  loadCheckboxes(): void {
    this.getSectorData();
    this.getAdvertTypes();
    this.getAdvertTypeHierarchy();
  }

  getAdvertPrePopulatedData(): void {
    try {
      this.loading = true;

      this.advertService.apiV1AdvertGetAdvertFilterDataByListingIdGet({ propertyListingId: this.selectedListing.id })
        .pipe(
          catchError((err) =>
            of(this.notificationService.showErrorMessage("Error", "Could not get the advert develop-able data."))
          ),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((result: any | void) => {
          this.advertFilterDTO = result ?? {};
          this.prePopulatedDataLoaded = true;
          this.applyPrePopulatedDataIfReady();
        });
    }
    catch (e) {
      this.loading = false;
      this.notificationService.showErrorMessage('Error', 'Could not load advert location data.');
    }
  }

  private applyPrePopulatedDataIfReady(): void {
    if (!this.sectorsLoaded || !this.typesLoaded || !this.hierarchyLoaded || !this.prePopulatedDataLoaded) {
      return;
    }
    if (this.hierarchyCategoryIds.size > 0) {
      this.advertTypesList = this.advertTypesList.filter(t => !t.id || this.hierarchyCategoryIds.has(t.id));
    }
    this.setPrePopulatedFields();
  }

  setPrePopulatedFields(): void {
    this.advertFilterDTOid = this.advertFilterDTO.id;
    this.sellerAskingPriceExcludeVATcommission = this.advertFilterDTO?.sellerAskingPriceExcludeVATCommission;
    this.SgErfExtent = this.advertFilterDTO?.sgErfExtent;

    const savedSectors = this.advertFilterDTO.advertSector ?? [];
    const savedTypes = this.advertFilterDTO.advertTypes ?? [];
    const savedItems = this.advertFilterDTO.advertTypeItems ?? [];
    const hasAnySectorSaved = savedSectors.length > 0;
    const includesBothSectors = savedSectors.some(s => s.description === 'Both');

    (['Developed', 'Developable'] as AdvertFilterSector[]).forEach(description => {
      const sector = this.advertSectorList.find(s => s.description === description);
      if (!sector) {
        return;
      }
      const wasSaved = includesBothSectors || savedSectors.some(s => s.description === description);
      if (wasSaved || !hasAnySectorSaved) {
        sector.isChecked = true;
      }
    });
    this.syncOptOutFromSectors();

    savedTypes.forEach(savedType => {
      const match = this.advertTypesList.find(t => t.description === savedType.description);
      if (match?.id) {
        this.developableTypeIds.add(match.id);
      }
    });

    savedItems.forEach(savedItem => {
      if (savedItem.id) {
        this.developedItemIds.add(savedItem.id);
      }
    });
  }

  setPropertyListingTask(): void {
    this.propertyListingService
      .apiV1PropertyListingGetPropertyListingByIdGet({ listingId: this.selectedListing.id })
      .subscribe(freshListing => {

        this.selectedListing = freshListing;

        const changedTaskLists = this.selectedListing.taskList
          ?.filter(task =>
            task.taskNumber?.number === 10 &&
            task.taskStatus?.taskStatusNumber !== 3
          )
          .map(task => ({
            ...task,
            user: this.currentUser
          })) ?? [];

        if (changedTaskLists.length > 0) {
          this.propertyListingService
            .apiV1PropertyListingUpdatePropertyListingTaskPost({
              body: changedTaskLists,
              currentTaskNumber: 10
            })
            .subscribe(result => {
              if (result != null) {
                this.navigateToPlanningReport();
              } else {
                this.notificationService.showErrorMessage('Error', 'Failed to update planners.');
              }
            });
        } else {
          this.navigateToPlanningReport();
        }

      });
  }

  getCurrentUser(): void {
    this.userManagementService.apiV1UserManagementGetCurrentUserGet().subscribe(result => {
      if (result != null) {
        this.currentUser = result;
      }
      else {
        this.notificationService.showWarningMessage('No Current User', 'No current user found.');
      }
    });
  }

  navigateToPlanningReport() {
    this.propertyListingService.apiV1PropertyListingGetPropertyListingByIdGet({ listingId: this.selectedListing.id }).subscribe(result => {
      this.selectedListing = result;
      this.listingStateService.setSelectedListing(this.selectedListing);
      this.router.navigate(['admin/listings/listing-overview'], {
        queryParams: {
          listingType: this.listingType
        }
      });
    });
  }

  private getSectorData(): void {
    try {
      this.lookupService.apiV1LookupGetAdvertSectorsGet()
        .pipe(
          catchError((err) =>
            of(this.notificationService.showErrorMessage("Error", "Could not load advert sector data."))
          ),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((advertSectorList: AdvertSectorDto[] | void) => {
          if (advertSectorList && advertSectorList.length > 0) {
            this.advertSectorList = advertSectorList;
          }
          this.sectorsLoaded = true;
          this.applyPrePopulatedDataIfReady();
        });
    }
    catch (e) {
      this.notificationService.showErrorMessage('Error', 'Could not load advert location data.');
    }
  }

  private getAdvertTypes(): void {
    try {
      this.lookupService.apiV1LookupGetAdvertTypesGet()
        .pipe(
          catchError((err) =>
            of(this.notificationService.showErrorMessage("Error", "Could not load Advert types data."))
          ),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((advertTypesList: AdvertTypesDto[] | void) => {
          if (advertTypesList && advertTypesList.length > 0) {
            this.advertTypesList = advertTypesList;
          }
          this.typesLoaded = true;
          this.applyPrePopulatedDataIfReady();
        });
    }
    catch (e) {
      this.notificationService.showErrorMessage('Error', 'Could not load advert location data.');
    }
  }

  private getAdvertTypeHierarchy(): void {
    try {
      this.lookupService.apiV1LookupGetAdvertTypeHierarchyGet()
        .pipe(
          catchError((err) =>
            of(this.notificationService.showErrorMessage("Error", "Could not load advert type hierarchy data."))
          ),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe((hierarchy: AdvertTypesDto[] | void) => {
          if (hierarchy && hierarchy.length > 0) {
            this.developedTypeCategories = buildDevelopedTypeCategories(hierarchy);
            this.hierarchyCategoryIds = new Set(
              hierarchy.filter(c => (c.subgroups ?? []).length > 0 && !!c.id).map(c => c.id as string)
            );
          }
          this.hierarchyLoaded = true;
          this.applyPrePopulatedDataIfReady();
        });
    }
    catch (e) {
      this.notificationService.showErrorMessage('Error', 'Could not load advert type hierarchy data.');
    }
  }

  private clearSelectionsForSector(sector: AdvertFilterSector): void {
    if (sector === 'Developable') {
      this.developableTypeIds.clear();
    } else {
      this.developedItemIds.clear();
    }
  }

  private syncOptOutFromSectors(): void {
    (['Developed', 'Developable'] as AdvertFilterSector[]).forEach(description => {
      const sector = this.advertSectorList.find(s => s.description === description);
      if (sector) {
        this.optOut[description] = !sector.isChecked;
      }
    });
  }

  private syncSectorsFromOptOut(): void {
    (['Developed', 'Developable'] as AdvertFilterSector[]).forEach(description => {
      const sector = this.advertSectorList.find(s => s.description === description);
      if (sector) {
        sector.isChecked = !this.optOut[description];
      }
    });
  }

}
